// src/api/client.ts — Centralised HTTP client
//
// The API base is resolved exactly once, here, and every other module imports
// it. In production nothing is pinned: the bundle falls back to the origin it
// was served from, so the same image works on the default Cloud Run host and on
// https://form.rubyracingandbreeding.com.au without any call going cross-origin.
// Only the CRA dev server (which serves the SPA separately from the API) needs
// the explicit localhost:8000 hop.
import {
  clearAccessToken,
  csrfHeaders,
  getAccessToken,
  setAccessToken,
} from './tokenStore';

/** The subset of `window.location` the base-URL rule actually reads. */
export interface LocationLike {
  origin: string;
  hostname: string;
  port: string;
}

export function getDefaultBaseUrl(
  location: LocationLike | Location | null = typeof window !== 'undefined' ? window.location : null,
): string {
  if (location?.origin) {
    if (
      ['localhost', '127.0.0.1'].includes(location.hostname) &&
      ['3000', '3002'].includes(location.port)
    ) {
      return 'http://localhost:8000';
    }
    return location.origin;
  }
  return 'http://localhost:8000';
}

export function normalizeBaseUrl(url: string | null | undefined): string {
  return url ? url.replace(/\/+$/, '') : '';
}

const configuredBaseUrl =
  process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || '';
const BASE_URL = normalizeBaseUrl(configuredBaseUrl) || getDefaultBaseUrl();

/** Error payloads FastAPI returns on a non-2xx. */
export interface ApiErrorBody {
  detail?: string;
  [key: string]: unknown;
}

class ApiError extends Error {
  readonly status: number;
  readonly data: ApiErrorBody | null;

  constructor(message: string, status: number, data: ApiErrorBody | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function getToken(): string | null {
  return getAccessToken();
}

function clearAuth(): void {
  clearAccessToken();
  window.location.href = '/login';
}

// Attempt to obtain a fresh access token using the HttpOnly refresh cookie.
// De-duplicated so concurrent 401s only trigger a single refresh call.
let refreshPromise: Promise<string | null> | null = null;
async function refreshToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include', // send the refresh_token cookie
          headers: { ...csrfHeaders() },
        });
        if (!res.ok) return null;
        const data = (await res.json()) as { access_token?: string } | null;
        if (data?.access_token) {
          setAccessToken(data.access_token);
          return data.access_token;
        }
        return null;
      } catch {
        return null;
      } finally {
        // Clear the in-flight promise after it settles so future 401s retry.
        setTimeout(() => {
          refreshPromise = null;
        }, 0);
      }
    })();
  }
  return refreshPromise;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Bearer token is attached unless a public endpoint opts out. */
  requiresAuth?: boolean;
  /** Internal: set on the single post-refresh replay so it cannot loop. */
  _retried?: boolean;
}

/**
 * `T` is the caller's assertion about the response body — it is NOT validated at
 * runtime. Keep it in step with the FastAPI response model; the typed wrappers in
 * the sibling modules are the single place each endpoint's shape is declared.
 */
async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body = null, headers = {}, requiresAuth = true, _retried = false } = options;

  const requestHeaders: Record<string, string> = { 'Content-Type': 'application/json', ...headers };
  const config: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include',
  };

  if (requiresAuth) {
    const token = getToken();
    if (!token) {
      clearAuth();
      throw new ApiError('No auth token', 401);
    }
    requestHeaders.Authorization = `Bearer ${token}`;
  }

  if (body) config.body = JSON.stringify(body);

  const url = endpoint.startsWith('http') ? endpoint : `${BASE_URL}${endpoint}`;

  try {
    const res = await fetch(url, config);
    if (res.status === 401) {
      // Try to refresh the access token once, then replay the original request.
      if (requiresAuth && !_retried) {
        const newToken = await refreshToken();
        if (newToken) {
          return request<T>(endpoint, { ...options, _retried: true });
        }
      }
      clearAuth();
      throw new ApiError('Session expired', 401);
    }
    if (!res.ok) {
      const errorData = (await res.json().catch(() => null)) as ApiErrorBody | null;
      throw new ApiError(errorData?.detail || `Request failed: ${res.statusText}`, res.status, errorData);
    }
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/pdf')) return (await res.blob()) as T;
    return (await res.json()) as T;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(err instanceof Error ? err.message : 'Network error', 0);
  }
}

export const api = {
  get: <T = unknown>(endpoint: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...opts, method: 'GET' }),
  post: <T = unknown>(endpoint: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...opts, method: 'POST', body }),
  put: <T = unknown>(endpoint: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...opts, method: 'PUT', body }),
  delete: <T = unknown>(endpoint: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...opts, method: 'DELETE' }),
};

export { ApiError, BASE_URL, refreshToken as refreshAccessToken };
export default api;
